/**
 * View models for GreenPak Trails.
 *
 * Every decision about what a trail page or card SHOWS is made here, in
 * plain functions with no React and no database, so the rules that matter
 * most — never print a fact nobody verified, never offer a filter the data
 * cannot answer, never render an empty section heading — are testable
 * without a browser. The components below this layer are dumb: they receive
 * a list and render it.
 *
 * The input type is structural rather than Prisma's, so these run in the
 * node test environment and so a future non-DB source (an import preview, a
 * fixture) can be passed through the same rules.
 */
import { googleMapsHref, GOOGLE_MAPS_LINKS_ENABLED } from "./google-maps-link";
import {
  buildTrailDifficultyFactors,
  buildTrailDifficultyMeter,
  type TrailDifficultyFactorModel,
  type TrailDifficultyMeterModel,
} from "./trail-difficulty";
import {
  buildTrailFacilities,
  buildTrailStatus,
  buildTrailTrust,
  formatTrailDistance,
  formatTrailDuration,
  formatTrailElevation,
  trailActivityLabel,
  trailBestForLabel,
  trailRouteTypeHint,
  trailRouteTypeLabel,
  trailShadeLabel,
  trailWaypointKindLabel,
  type TrailFacilityModel,
  type TrailStatusModel,
  type TrailTrustModel,
} from "./trail-taxonomy";
import { trailHref } from "./trail-collections";

export type TrailWaypointInput = {
  order: number;
  name: string;
  kind: string;
  note: string | null;
  lat: number | null;
  lng: number | null;
  distanceFromStartKm: number | null;
  elevationM: number | null;
  sightSlug: string | null;
};

/** The subset of a Trail row every presentation rule below actually reads. */
export type TrailViewInput = {
  slug: string;
  name: string;
  alternateName: string | null;
  collectionSlug: string;
  summary: string;
  description: string | null;
  city: string;
  province: string;
  protectedArea: string | null;
  activityTypes: string[];
  bestFor: string[];

  /** Optional Google Maps override for the trailhead; empty derives it from
   *  trailheadLat/Lng. See lib/google-maps-link.ts. */
  googleMapsUrl: string | null;
  trailheadName: string | null;
  trailheadLat: number | null;
  trailheadLng: number | null;
  endName: string | null;
  endLat: number | null;
  endLng: number | null;
  routeType: string | null;
  distanceKm: number | null;
  distanceSource: string | null;
  elevationGainM: number | null;
  elevationLossM: number | null;
  minElevationM: number | null;
  maxElevationM: number | null;
  elevationSource: string | null;
  routeGeometrySource: string | null;
  routeGeometryAttribution: string | null;
  routeGeometry: unknown;

  difficulty: number | null;
  difficultyNote: string | null;
  difficultyRationale: string | null;
  factorSteepness: number | null;
  factorEndurance: number | null;
  factorTerrain: number | null;
  factorNavigation: number | null;

  durationMinMinutes: number | null;
  durationMaxMinutes: number | null;
  durationSource: string | null;

  trailheadDescription: string | null;
  directions: string | null;
  parking: string | null;
  publicTransport: string | null;
  feeInfo: string | null;
  permitInfo: string | null;
  accessibilityInfo: string | null;

  surface: string | null;
  shadeLevel: string | null;
  viewpoints: string[];
  naturalFeatures: string[];
  culturalPoints: string[];
  wildlifeNotes: string | null;
  seasonalNotes: string | null;
  bestTimeOfDay: string | null;
  photographyNotes: string | null;

  facilityWater: string | null;
  facilityToilets: string | null;
  facilityFood: string | null;
  facilityShelter: string | null;
  facilitiesNote: string | null;

  fitnessGuidance: string | null;
  equipmentGuidance: string[];
  waterGuidance: string | null;
  weatherGuidance: string | null;
  wildlifeGuidance: string | null;
  parkRules: string[];
  warnings: string[];

  status: string;
  statusSource: string | null;
  statusCheckedAt: Date | string | null;
  alertText: string | null;
  alertSource: string | null;
  alertUrl: string | null;

  verificationState: string;
  sourceOrganization: string | null;
  sourceUrl: string | null;
  sourceCheckedAt: Date | string | null;
  fieldCheckedAt: Date | string | null;

  heroImage: string | null;
  heroImageCaption: string | null;
  heroImageCredit: string | null;
  heroImageCreditUrl: string | null;
  gallery: unknown;

  waypoints?: TrailWaypointInput[];
};

export type TrailFact = {
  key: string;
  label: string;
  value: string;
  /** Where the number came from, when the row records it. */
  source?: string | null;
  /** Extra plain-language meaning, e.g. what "out and back" implies. */
  hint?: string | null;
};

const nonEmpty = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const nonEmptyList = (values: string[] | null | undefined): string[] =>
  (values ?? []).map((v) => v.trim()).filter(Boolean);

/**
 * The facts panel. Only entries with a real value survive, so a trail whose
 * distance nobody has measured shows four facts rather than five with a dash.
 */
export function buildTrailKeyFacts(trail: TrailViewInput): TrailFact[] {
  const facts: TrailFact[] = [];

  const distance = formatTrailDistance(trail.distanceKm);
  if (distance) {
    facts.push({
      key: "distance",
      label: "Distance",
      value: distance,
      // The only distance held is qualified ("4 km up to Jabbi"), so the
      // source line is what keeps the number honest on the facts panel.
      source: nonEmpty(trail.distanceSource),
    });
  }

  const duration = formatTrailDuration(trail.durationMinMinutes, trail.durationMaxMinutes);
  if (duration) {
    facts.push({
      key: "duration",
      label: "Typical time",
      value: duration,
      source: nonEmpty(trail.durationSource),
    });
  }

  const gain = formatTrailElevation(trail.elevationGainM);
  if (gain) {
    facts.push({
      key: "elevationGain",
      label: "Elevation gain",
      value: gain,
      source: nonEmpty(trail.elevationSource),
    });
  }

  const high = formatTrailElevation(trail.maxElevationM);
  if (high) {
    facts.push({
      key: "maxElevation",
      label: "High point",
      value: high,
      source: nonEmpty(trail.elevationSource),
    });
  }

  const routeType = trailRouteTypeLabel(trail.routeType);
  if (routeType) {
    facts.push({
      key: "routeType",
      label: "Route type",
      value: routeType,
      hint: trailRouteTypeHint(trail.routeType),
    });
  }

  const shade = trailShadeLabel(trail.shadeLevel);
  if (shade) facts.push({ key: "shade", label: "Shade", value: shade });

  return facts;
}

export type TrailCardModel = {
  slug: string;
  href: string;
  name: string;
  /** "Margalla Hills National Park · Islamabad" */
  location: string;
  summary: string;
  image: string | null;
  imageAlt: string;
  difficulty: TrailDifficultyMeterModel | null;
  /** At most three, chosen for scanability rather than completeness. */
  facts: TrailFact[];
  status: TrailStatusModel;
  /** Only surfaced on a card when it is something the reader must know. */
  showStatusFlag: boolean;
  trust: TrailTrustModel;
  bestFor: string[];
};

export function buildTrailCard(trail: TrailViewInput): TrailCardModel {
  const status = buildTrailStatus(trail);
  const locationParts = [nonEmpty(trail.protectedArea), nonEmpty(trail.city)].filter(
    Boolean,
  ) as string[];

  return {
    slug: trail.slug,
    href: trailHref(trail.collectionSlug, trail.slug),
    name: trail.name,
    location: locationParts.join(" · "),
    summary: trail.summary,
    image: nonEmpty(trail.heroImage),
    imageAlt: nonEmpty(trail.heroImageCaption) ?? trail.name,
    difficulty: buildTrailDifficultyMeter({
      level: trail.difficulty,
      note: trail.difficultyNote,
    }),
    facts: buildTrailKeyFacts(trail)
      .filter((fact) => fact.key === "distance" || fact.key === "duration" || fact.key === "elevationGain")
      .slice(0, 3),
    status,
    // An OPEN trail needs no card badge — it is the unremarkable case. Closed,
    // restricted and unconfirmed all change whether someone should set off, so
    // they travel with the card into every list it appears in.
    showStatusFlag: status.status !== "OPEN",
    trust: buildTrailTrust(trail),
    bestFor: nonEmptyList(trail.bestFor).map(trailBestForLabel),
  };
}

export type TrailSection = {
  id: string;
  /** Title Case, per the site-wide heading rule. */
  title: string;
  kind: "prose" | "list" | "facts" | "waypoints" | "map" | "gallery";
};

/**
 * Which detail sections have something to say.
 *
 * The page renders exactly these, in this order, and nothing else — so a
 * trail with no wildlife information has no "Wildlife & Nature" heading
 * followed by white space. The in-page navigation is built from the same
 * list, so it can never link to a section that is not there.
 */
export function buildTrailSections(trail: TrailViewInput): TrailSection[] {
  const sections: TrailSection[] = [];
  const push = (id: string, title: string, kind: TrailSection["kind"], has: boolean) => {
    if (has) sections.push({ id, title, kind });
  };

  push("overview", "Overview", "prose", Boolean(nonEmpty(trail.description)));
  push("facts", "Difficulty & Stats", "facts", buildTrailKeyFacts(trail).length > 0 || trail.difficulty !== null);
  push("map", "Trail Map", "map", hasMappableRoute(trail));
  push(
    "expect",
    "What to Expect",
    "prose",
    Boolean(
      nonEmpty(trail.surface) ||
        nonEmptyList(trail.viewpoints).length ||
        nonEmptyList(trail.naturalFeatures).length ||
        nonEmptyList(trail.culturalPoints).length ||
        nonEmpty(trail.photographyNotes),
    ),
  );
  push("route", "Route & Key Waypoints", "waypoints", (trail.waypoints ?? []).length > 0);
  push(
    "getting-there",
    "Getting There",
    "prose",
    Boolean(
      nonEmpty(trail.trailheadDescription) ||
        nonEmpty(trail.directions) ||
        nonEmpty(trail.parking) ||
        nonEmpty(trail.publicTransport) ||
        // A trail with a pinned trailhead and no prose still has something
        // useful to say here: where it starts, openable in Google Maps. Only
        // while that link actually works, though — otherwise this opens a
        // section whose entire contents are one inert line.
        (GOOGLE_MAPS_LINKS_ENABLED &&
          googleMapsHref({
            googleMapsUrl: trail.googleMapsUrl,
            lat: trail.trailheadLat,
            lng: trail.trailheadLng,
          })),
    ),
  );
  push(
    "before-you-go",
    "Before You Go",
    "prose",
    Boolean(
      nonEmpty(trail.feeInfo) ||
        nonEmpty(trail.permitInfo) ||
        nonEmpty(trail.accessibilityInfo) ||
        nonEmpty(trail.seasonalNotes) ||
        nonEmpty(trail.bestTimeOfDay) ||
        buildTrailFacilities(trail).length > 0 ||
        nonEmpty(trail.facilitiesNote),
    ),
  );
  push(
    "safety",
    "Safety",
    "list",
    Boolean(
      nonEmptyList(trail.warnings).length ||
        nonEmptyList(trail.parkRules).length ||
        nonEmptyList(trail.equipmentGuidance).length ||
        nonEmpty(trail.fitnessGuidance) ||
        nonEmpty(trail.waterGuidance) ||
        nonEmpty(trail.weatherGuidance),
    ),
  );
  push("gallery", "Gallery", "gallery", buildTrailGallery(trail).length > 0);
  push(
    "nature",
    "Wildlife & Nature",
    "prose",
    Boolean(nonEmpty(trail.wildlifeNotes) || nonEmpty(trail.wildlifeGuidance)),
  );

  return sections;
}

/**
 * True only when there is genuinely something to draw.
 *
 * A trailhead with no coordinates is still worth describing in words, but it
 * cannot be put on a map — and an empty map framed as a trail map is worse
 * than no map. Two mappable points (or a trailhead plus a mapped waypoint)
 * is the minimum that produces a line rather than a lone dot.
 */
export function hasMappableRoute(trail: TrailViewInput): boolean {
  return buildTrailRouteLine(trail).length >= 2 || buildTrailRoutePoints(trail).length >= 1;
}

export type TrailRoutePoint = {
  order: number;
  name: string;
  lat: number;
  lng: number;
  kind: string;
};

/**
 * The ordered, actually-locatable points of a trail.
 *
 * Waypoints without coordinates are dropped here and only here — they still
 * appear in the written route list, because "the second ridge junction" is
 * useful to a walker even when nobody has surveyed it. Nothing in this
 * function invents a position.
 */
export function buildTrailRoutePoints(trail: TrailViewInput): TrailRoutePoint[] {
  const points: TrailRoutePoint[] = [];
  const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

  if (isNum(trail.trailheadLat) && isNum(trail.trailheadLng)) {
    points.push({
      order: 0,
      name: nonEmpty(trail.trailheadName) ?? "Trailhead",
      lat: trail.trailheadLat,
      lng: trail.trailheadLng,
      kind: "TRAILHEAD",
    });
  }

  for (const waypoint of (trail.waypoints ?? []).slice().sort((a, b) => a.order - b.order)) {
    if (!isNum(waypoint.lat) || !isNum(waypoint.lng)) continue;
    points.push({
      order: waypoint.order + 1,
      name: waypoint.name,
      lat: waypoint.lat,
      lng: waypoint.lng,
      kind: waypoint.kind,
    });
  }

  if (isNum(trail.endLat) && isNum(trail.endLng)) {
    points.push({
      order: points.length + 1,
      name: nonEmpty(trail.endName) ?? "End point",
      lat: trail.endLat,
      lng: trail.endLng,
      kind: "END",
    });
  }

  return points.map((point, index) => ({ ...point, order: index }));
}

/**
 * The surveyed route line, validated on the way out.
 *
 * Stored as JSON, so it is checked rather than trusted: anything that is not
 * an array of at least two [lat, lng] pairs inside Pakistan's bounding box is
 * treated as absent. A malformed line would otherwise draw a route across the
 * Arabian Sea, and a map is exactly where a reader stops checking.
 */
export function buildTrailRouteLine(trail: TrailViewInput): Array<[number, number]> {
  const raw = trail.routeGeometry;
  if (!Array.isArray(raw) || raw.length < 2) return [];
  const line: Array<[number, number]> = [];
  for (const entry of raw) {
    if (!Array.isArray(entry) || entry.length < 2) return [];
    const [lat, lng] = entry;
    if (typeof lat !== "number" || typeof lng !== "number") return [];
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
    if (lat < 23 || lat > 38 || lng < 60 || lng > 78) return [];
    line.push([lat, lng]);
  }
  return line;
}

export type TrailGalleryImage = {
  url: string;
  caption: string | null;
  credit: string | null;
  creditUrl: string | null;
};

/**
 * The gallery, validated on the way out.
 *
 * Stored as JSON, so it is checked rather than trusted: anything without a
 * usable `url` is dropped instead of rendering a broken tile, and a malformed
 * column yields an empty gallery rather than throwing on a page a visitor is
 * reading. Captions and credits are optional — a photograph GreenPak took
 * needs no credit, one from elsewhere does, and the difference is the
 * editor's to state.
 */
export function buildTrailGallery(trail: TrailViewInput): TrailGalleryImage[] {
  const raw = trail.gallery;
  if (!Array.isArray(raw)) return [];
  const images: TrailGalleryImage[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const row = entry as Record<string, unknown>;
    const url = typeof row.url === "string" ? row.url.trim() : "";
    if (!url) continue;
    const str = (value: unknown) =>
      typeof value === "string" && value.trim() ? value.trim() : null;
    images.push({
      url,
      caption: str(row.caption),
      credit: str(row.credit),
      creditUrl: str(row.creditUrl),
    });
  }
  return images;
}

export type TrailWaypointModel = {
  order: number;
  name: string;
  kindLabel: string;
  note: string | null;
  distanceLabel: string | null;
  elevationLabel: string | null;
  sightSlug: string | null;
  mapped: boolean;
};

export function buildTrailWaypoints(trail: TrailViewInput): TrailWaypointModel[] {
  return (trail.waypoints ?? [])
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((waypoint) => ({
      order: waypoint.order,
      name: waypoint.name,
      kindLabel: trailWaypointKindLabel(waypoint.kind),
      note: nonEmpty(waypoint.note),
      distanceLabel: formatTrailDistance(waypoint.distanceFromStartKm),
      elevationLabel: formatTrailElevation(waypoint.elevationM),
      sightSlug: nonEmpty(waypoint.sightSlug),
      mapped: typeof waypoint.lat === "number" && typeof waypoint.lng === "number",
    }));
}

export type TrailDetailModel = {
  card: TrailCardModel;
  sections: TrailSection[];
  facts: TrailFact[];
  difficulty: TrailDifficultyMeterModel | null;
  difficultyRationale: string | null;
  difficultyFactors: TrailDifficultyFactorModel[];
  facilities: TrailFacilityModel[];
  routePoints: TrailRoutePoint[];
  routeLine: Array<[number, number]>;
  routeAttribution: string | null;
  waypoints: TrailWaypointModel[];
  gallery: TrailGalleryImage[];
  activities: string[];
  /**
   * True when the trail has a written route but no geometry at all — the page
   * says so plainly instead of leaving a reader to assume the line is missing
   * by accident.
   */
  routeGeometryMissing: boolean;
};

export function buildTrailDetail(trail: TrailViewInput): TrailDetailModel {
  const routePoints = buildTrailRoutePoints(trail);
  const routeLine = buildTrailRouteLine(trail);
  return {
    card: buildTrailCard(trail),
    sections: buildTrailSections(trail),
    facts: buildTrailKeyFacts(trail),
    difficulty: buildTrailDifficultyMeter({ level: trail.difficulty, note: trail.difficultyNote }),
    difficultyRationale: nonEmpty(trail.difficultyRationale),
    difficultyFactors: buildTrailDifficultyFactors({
      steepness: trail.factorSteepness,
      endurance: trail.factorEndurance,
      terrain: trail.factorTerrain,
      navigation: trail.factorNavigation,
    }),
    facilities: buildTrailFacilities(trail),
    routePoints,
    routeLine,
    routeAttribution: nonEmpty(trail.routeGeometryAttribution),
    waypoints: buildTrailWaypoints(trail),
    gallery: buildTrailGallery(trail),
    activities: nonEmptyList(trail.activityTypes).map(trailActivityLabel),
    routeGeometryMissing: routeLine.length === 0,
  };
}

// ---------- Discovery filters ----------

export type TrailFilterState = {
  difficulty: number[];
  routeType: string[];
  bestFor: string[];
  /** Bucket keys from TRAIL_DURATION_BUCKETS. */
  duration: string[];
};

export const EMPTY_TRAIL_FILTERS: TrailFilterState = {
  difficulty: [],
  routeType: [],
  bestFor: [],
  duration: [],
};

export const TRAIL_DURATION_BUCKETS = [
  { key: "under-90", label: "Under 1.5 hr", maxMinutes: 90, minMinutes: 0 },
  { key: "90-180", label: "1.5–3 hr", minMinutes: 90, maxMinutes: 180 },
  { key: "over-180", label: "Over 3 hr", minMinutes: 180, maxMinutes: Number.POSITIVE_INFINITY },
] as const;

/** A trail lands in a bucket if any part of its range overlaps that bucket. */
function trailDurationBuckets(trail: TrailViewInput): string[] {
  const min = trail.durationMinMinutes ?? trail.durationMaxMinutes;
  const max = trail.durationMaxMinutes ?? trail.durationMinMinutes;
  if (typeof min !== "number" || typeof max !== "number") return [];
  return TRAIL_DURATION_BUCKETS.filter(
    (bucket) => min < bucket.maxMinutes && max >= bucket.minMinutes,
  ).map((bucket) => bucket.key);
}

export type TrailFilterGroup = {
  key: keyof TrailFilterState;
  label: string;
  options: Array<{ value: string; label: string; count: number }>;
};

/**
 * Filters built from the trails actually present.
 *
 * An option only exists if at least one trail can answer it, so the page
 * never offers "Under 1.5 hr" against a catalogue where no duration has been
 * verified, and a whole group disappears rather than rendering empty. This is
 * the filter-side expression of the same rule the facts panel follows.
 */
export function buildTrailFilterGroups(trails: TrailViewInput[]): TrailFilterGroup[] {
  const groups: TrailFilterGroup[] = [];

  const difficultyCounts = new Map<number, number>();
  const routeTypeCounts = new Map<string, number>();
  const bestForCounts = new Map<string, number>();
  const durationCounts = new Map<string, number>();

  for (const trail of trails) {
    if (typeof trail.difficulty === "number") {
      difficultyCounts.set(trail.difficulty, (difficultyCounts.get(trail.difficulty) ?? 0) + 1);
    }
    if (trailRouteTypeLabel(trail.routeType)) {
      const key = trail.routeType as string;
      routeTypeCounts.set(key, (routeTypeCounts.get(key) ?? 0) + 1);
    }
    for (const tag of nonEmptyList(trail.bestFor)) {
      bestForCounts.set(tag, (bestForCounts.get(tag) ?? 0) + 1);
    }
    for (const bucket of trailDurationBuckets(trail)) {
      durationCounts.set(bucket, (durationCounts.get(bucket) ?? 0) + 1);
    }
  }

  if (difficultyCounts.size > 0) {
    groups.push({
      key: "difficulty",
      label: "Difficulty",
      options: [...difficultyCounts.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([level, count]) => ({
          value: String(level),
          label: `${level} · ${buildTrailDifficultyMeter({ level })?.label ?? level}`,
          count,
        })),
    });
  }

  if (durationCounts.size > 0) {
    groups.push({
      key: "duration",
      label: "Time on foot",
      options: TRAIL_DURATION_BUCKETS.filter((bucket) => durationCounts.has(bucket.key)).map(
        (bucket) => ({
          value: bucket.key,
          label: bucket.label,
          count: durationCounts.get(bucket.key) ?? 0,
        }),
      ),
    });
  }

  if (routeTypeCounts.size > 0) {
    groups.push({
      key: "routeType",
      label: "Route type",
      options: [...routeTypeCounts.entries()].map(([value, count]) => ({
        value,
        label: trailRouteTypeLabel(value) ?? value,
        count,
      })),
    });
  }

  if (bestForCounts.size > 0) {
    groups.push({
      key: "bestFor",
      label: "Best for",
      options: [...bestForCounts.entries()].map(([value, count]) => ({
        value,
        label: trailBestForLabel(value),
        count,
      })),
    });
  }

  return groups;
}

/**
 * The only fields filtering needs, precomputed on the server.
 *
 * The discovery page renders its cards server-side and ships these alongside,
 * rather than shipping whole trail records and rebuilding cards in the
 * browser. That keeps the payload small and, more importantly, keeps
 * buildTrailTrust's `new Date()` on one side of the boundary — a card built
 * on the server and rebuilt on the client could otherwise disagree about
 * staleness and trip a hydration mismatch.
 */
export type TrailFacets = {
  slug: string;
  difficulty: number | null;
  routeType: string | null;
  bestFor: string[];
  durationBuckets: string[];
};

export function buildTrailFacets(trail: TrailViewInput): TrailFacets {
  return {
    slug: trail.slug,
    difficulty: typeof trail.difficulty === "number" ? trail.difficulty : null,
    routeType: trailRouteTypeLabel(trail.routeType) ? (trail.routeType as string) : null,
    bestFor: nonEmptyList(trail.bestFor),
    durationBuckets: trailDurationBuckets(trail),
  };
}

/** AND across groups, OR within a group — the usual faceted-search reading. */
export function matchesTrailFacets(facets: TrailFacets, filters: TrailFilterState): boolean {
  if (filters.difficulty.length && !filters.difficulty.includes(facets.difficulty ?? -1)) return false;
  if (filters.routeType.length && !filters.routeType.includes(facets.routeType ?? "")) return false;
  if (filters.bestFor.length && !filters.bestFor.some((tag) => facets.bestFor.includes(tag))) {
    return false;
  }
  if (
    filters.duration.length &&
    !filters.duration.some((bucket) => facets.durationBuckets.includes(bucket))
  ) {
    return false;
  }
  return true;
}

export function matchesTrailFilters(trail: TrailViewInput, filters: TrailFilterState): boolean {
  return matchesTrailFacets(buildTrailFacets(trail), filters);
}
